import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiError, ERROR_CODES } from '@/shared/api/http';
import type { EntityReference } from '@/shared/api/requests';
import type { WorkType } from '@/shared/api/serviceCatalog';
import type { TaskSummary } from '@/shared/api/tasks';
import {
  timeEntriesApi,
  timersApi,
  type ApprovalStatus,
  type TimeEntryDetail,
  type TimeEntryPage,
  type TimeEntrySummary,
  type UpdateTimeEntryInput,
} from '@/shared/api/timeTracking';
import { getActiveFirstDayOfWeek, startOfCurrentWeek, type DayOfWeek } from '@/shared/week';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The views of "Mis horas" (13 §4.6): each one is a set of filters the endpoint already admits. */
export const TIME_VIEWS = ['pending', 'week', 'all'] as const;

export type TimeView = (typeof TIME_VIEWS)[number];

export function approvalColor(status: ApprovalStatus): string {
  switch (status) {
    case 'approved':
      return 'teal';
    case 'rejected':
      return 'red';
    default:
      return 'yellow';
  }
}

/** Who is looking at the hours and what they may do with them, as the console reads it from the session. */
export interface TimeEntryViewer {
  userId: string | null | undefined;
  /** `time_entries.manage`: edits and withdraws the hours of anybody. */
  canManage: boolean;
  /** `time_entries.approve`. */
  canApprove: boolean;
  /** `time_entries.approve_own`: may approve an hour that is theirs. */
  canApproveOwn: boolean;
  /** Whether the tenant reviews hours at all (`AM-203`). */
  approvalRequired: boolean;
}

type EditGuards = Pick<TimeEntrySummary, 'agent' | 'billingStatus' | 'isLocked'>;

/**
 * Whether the console offers "Editar" on an hour: the guards of the edit, which the server applies
 * again (`AM-294`, `TimeEntry.Amend`). Nothing locked, inside a period or billed (those are
 * corrected with "Ajustar"); whatever the approval says; and somebody else's only with
 * `time_entries.manage`.
 */
export function canEditEntry(
  entry: EditGuards,
  viewer: Pick<TimeEntryViewer, 'userId' | 'canManage'>,
): boolean {
  if (entry.isLocked || entry.billingStatus === 'in_period' || entry.billingStatus === 'billed') {
    return false;
  }
  return viewer.canManage || (viewer.userId != null && entry.agent.id === viewer.userId);
}

/**
 * Whether the console offers "Borrar" on an hour: the same guards as {@link canEditEntry}, because
 * withdrawing is the last correction the author can make (`AM-294`). An approved hour is withdrawn
 * too; the dialog says what that takes away.
 */
export function canDeleteEntry(
  entry: EditGuards,
  viewer: Pick<TimeEntryViewer, 'userId' | 'canManage'>,
): boolean {
  return canEditEntry(entry, viewer);
}

/**
 * `AM-370`, and since `AM-382` on every ticket: whether the "Tiempo" tab lets this viewer tick or
 * untick "Facturable" on the hour with one click, the quick way to decide whether its work is charged.
 * It is an edit of the hour and takes its guards (nothing locked, inside a period or billed; somebody
 * else's only with `time_entries.manage`); "Editar" and "Borrar" stay on the row as before.
 */
export function canToggleBillable(
  entry: EditGuards,
  viewer: Pick<TimeEntryViewer, 'userId' | 'canManage'>,
): boolean {
  return canEditEntry(entry, viewer);
}

/**
 * `AM-463`: who approved an approved hour, as the model calls it: "Aprobada por" a person, or "Sin revisión" when
 * the tenant does not require approval and the hour was born approved. `null` for a pending or rejected hour.
 */
export function approvedByOf(
  entry: Pick<TimeEntrySummary, 'approvalStatus' | 'approvalSource' | 'approvedBy'>,
  t: Translate,
): string | null {
  if (entry.approvalStatus !== 'approved') {
    return null;
  }
  const name =
    entry.approvalSource === 'automatic'
      ? t('time:approval.noReview')
      : (entry.approvedBy?.displayName ?? null);
  return name === null ? null : t('time:approval.approvedBy', { name });
}

/**
 * `AM-463`: whether the row offers "Volver a Pendiente": an approved hour in no document (not En borrador, not
 * Facturada), for whoever approves hours, where the tenant reviews them. The server applies the same guard.
 */
export function canUnapprove(
  entry: Pick<TimeEntrySummary, 'approvalStatus' | 'billingStatus' | 'isLocked'>,
  viewer: Pick<TimeEntryViewer, 'canApprove' | 'approvalRequired'>,
): boolean {
  return (
    viewer.canApprove &&
    viewer.approvalRequired &&
    entry.approvalStatus === 'approved' &&
    !entry.isLocked &&
    entry.billingStatus !== 'in_period' &&
    entry.billingStatus !== 'billed'
  );
}

/** Who an hour belongs to, for {@link editKeepsApproval}: its agent and whoever wrote it. */
interface HourAuthors {
  agent: EntityReference;
  recordedBy?: EntityReference | null;
}

/**
 * `AM-463`: whether the hour is the viewer's, as the server reads it (`TimeEntry.MayBeApprovedBy`): they are its
 * agent or they wrote it. Aprobación de horas uses it to say which rows the viewer may not approve.
 */
export function isOwnHour(entry: HourAuthors, userId: string | null | undefined): boolean {
  return userId != null && (entry.agent.id === userId || entry.recordedBy?.id === userId);
}

/**
 * `AM-382`, rule 5: whether ticking or unticking "Facturable" on this hour sends it back to
 * "pendiente" (`AM-294`, the rule of any change of the billing intent): it is approved and the viewer
 * may not keep it approved ({@link editKeepsApproval}). The tab asks before the click, not after.
 */
export function billableToggleReturnsToApproval(
  entry: HourAuthors & Pick<TimeEntrySummary, 'approvalStatus'>,
  viewer: Pick<TimeEntryViewer, 'userId' | 'canApprove' | 'canApproveOwn' | 'approvalRequired'>,
): boolean {
  return entry.approvalStatus === 'approved' && !editKeepsApproval(entry, viewer);
}

/**
 * Whether an edit that changes the duration, day, task, work type or billing intent of an approved
 * hour leaves it approved, in the name of whoever edits it (`AM-294`, `TimeEntry.Reapprove`). Where
 * the tenant does not review hours it stands approved by itself; where it does, only if the editor
 * may approve it: `time_entries.approve` and, when the hour is theirs (they are its agent or the
 * one who wrote it), `time_entries.approve_own` too. Otherwise it goes back to "pendiente". The
 * server decides; the console only needs the answer to warn before saving.
 */
export function editKeepsApproval(
  detail: HourAuthors,
  viewer: Pick<TimeEntryViewer, 'userId' | 'canApprove' | 'canApproveOwn' | 'approvalRequired'>,
): boolean {
  if (!viewer.approvalRequired) {
    return true;
  }
  const isOwn = isOwnHour(detail, viewer.userId);
  return viewer.canApprove && (!isOwn || viewer.canApproveOwn);
}

/**
 * The column "Facturación" of an hour (D3 of the billing model), on every internal screen of hours
 * ("Tiempo" of a ticket, its "Editar", "Mis horas", "Aprobación de horas"). The server works it out
 * once (`HourBillingValues`) and the console shows it as it comes: "Espera aprobación" is the hour with
 * a price that nobody approved yet, or that was rejected; "En borrador", the hour a close or "Facturar"
 * took whose document is not issued yet.
 */
export type BillingLabel =
  | 'not_billable'
  | 'contract_non_billable'
  | 'no_price'
  | 'awaiting_approval'
  | 'pending_billing'
  | 'in_draft'
  | 'billed'
  // `AM-394`: billed, and its document cancelled without billing the work again.
  | 'invoice_cancelled';

/** The values of {@link BillingLabel} in the order of the model: the order every screen lists them in. */
export const BILLING_LABELS: readonly BillingLabel[] = [
  'not_billable',
  'contract_non_billable',
  'no_price',
  'awaiting_approval',
  'pending_billing',
  'in_draft',
  'billed',
  'invoice_cancelled',
];

export function billingLabelOf(entry: Pick<TimeEntrySummary, 'billing'>): BillingLabel {
  return entry.billing as BillingLabel;
}

/**
 * `AM-386`: the reason of an unticked hour goes beside "No facturable" wherever a list of hours says its
 * "Facturación", so the row says why without opening the hour.
 */
export function billingReasonOf(
  entry: Pick<TimeEntrySummary, 'billing' | 'nonBillableReason'>,
): string | null {
  return entry.billing === 'not_billable' ? (entry.nonBillableReason?.displayName ?? null) : null;
}

/**
 * `AM-464`: why an hour "Sin precio" has no price and what to do, in the words of the model (pieza Motivo sin
 * precio), from the code of the reason the hour stores. `null` for any other hour, or a reason the model does not
 * name.
 */
export function noPriceReasonOf(
  entry: Pick<TimeEntrySummary, 'billing' | 'rateResolutionReason'>,
  t: Translate,
): string | null {
  const reason = entry.rateResolutionReason;
  if (entry.billing !== 'no_price' || reason == null || !NO_PRICE_REASONS.includes(reason)) {
    return null;
  }
  return t(`time:noPriceReason.${reason}`);
}

/**
 * `AM-552` (item 28 of the approved texts): what to do for the "Motivo sin precio" of an hour, whatever its
 * "Facturación" (in the list "Horas sin precio" of a period an hour can still be waiting for its approval).
 * `null` when the hour has no reason the model names.
 */
export function rateReasonOf(
  entry: Pick<TimeEntrySummary, 'rateResolutionReason'>,
  t: Translate,
): string | null {
  const reason = entry.rateResolutionReason;
  return reason == null || !NO_PRICE_REASONS.includes(reason)
    ? null
    : t(`time:noPriceReason.${reason}`);
}

const NO_PRICE_REASONS: readonly string[] = [
  'no_active_contract',
  'contract_not_effective_at_work_date',
  'no_matching_rate_card',
  'currency_mismatch',
  'ambiguous_rate_card',
  'ambiguous_contract',
];

/**
 * `AM-464`: the origin of the price of an hour, in the words of the model (pieza Origen del precio). `null` when the
 * hour has no price.
 */
export function priceOriginOf(
  entry: Pick<TimeEntrySummary, 'priceOrigin' | 'rateCard' | 'rateCardVersionFrom'>,
  t: Translate,
  formatDate: (day: string) => string,
): string | null {
  switch (entry.priceOrigin) {
    case 'rate_card':
      return entry.rateCard == null || entry.rateCardVersionFrom == null
        ? null
        : t('time:priceOrigin.rate_card', {
            name: entry.rateCard.displayName,
            from: formatDate(entry.rateCardVersionFrom),
          });
    case 'customer_rate':
    case 'general_rate':
    case 'covered_by_contract':
      return t(`time:priceOrigin.${entry.priceOrigin}`);
    default:
      return null;
  }
}

/**
 * `AM-464`: whether the row offers "Recalcular el precio": an hour "Sin precio" in no document, for whoever may ask for
 * it (the permissions of `resolve-rate`: `billing.invoice_tickets` or `costs.manage`, `AM-531`).
 */
export function canRecalculatePrice(
  entry: Pick<TimeEntrySummary, 'billing' | 'isLocked' | 'contract'>,
  permissions: ReadonlySet<string>,
  ticketContractId?: string | null,
): boolean {
  if (
    entry.isLocked ||
    !(permissions.has('billing.invoice_tickets') || permissions.has('costs.manage'))
  ) {
    return false;
  }
  if (entry.billing === 'no_price') {
    return true;
  }
  // `AM-665` (C09-35, C10-01): also an hour whose contract is no longer the one of its ticket, while no
  // document holds it (the server never prices again an hour of a period or of an invoice).
  return (
    ticketContractId !== undefined &&
    (entry.contract?.id ?? null) !== ticketContractId &&
    (entry.billing === 'pending_billing' ||
      entry.billing === 'awaiting_approval' ||
      entry.billing === 'contract_non_billable')
  );
}

/** The colour of a {@link BillingLabel} where it is a badge: charged in teal, waiting in yellow, without a price in orange. */
export function billingColor(label: BillingLabel): string {
  switch (label) {
    case 'pending_billing':
    case 'in_draft':
    case 'billed':
      return 'teal';
    case 'awaiting_approval':
      return 'yellow';
    case 'no_price':
      return 'orange';
    default:
      return 'gray';
  }
}

/**
 * `AM-382`, rule 2: what the console proposes for "Facturable" when it knows it, `undefined` when it
 * does not. An hour on a task needs that task among the tasks it read (the list may not have loaded,
 * may have failed, or may not have it): the server would take that task's box, so the work type is
 * no answer then. An hour without a task needs its work type among the work types it read.
 */
function knownProposal(
  choice: { taskId: string | null; workTypeId: string | null },
  tasks: readonly Pick<TaskSummary, 'id' | 'isBillable'>[] | undefined,
  workTypes: readonly Pick<WorkType, 'id' | 'defaultIsBillable'>[] | undefined,
): boolean | undefined {
  if (choice.taskId !== null) {
    return tasks?.find((candidate) => candidate.id === choice.taskId)?.isBillable;
  }
  return choice.workTypeId === null
    ? undefined
    : workTypes?.find((candidate) => candidate.id === choice.workTypeId)?.defaultIsBillable;
}

/**
 * `AM-382`, rule 2: what "Facturable" shows in a dialog that creates an hour ("Registrar tiempo",
 * "Detener", "Ticket rápido"), the very value {@link billableToSend} sends, so the box shows what is
 * saved. Once the person touched the box, what they chose. Untouched, what the console proposes when
 * it knows it ({@link knownProposal}): the task's "Facturable" for an hour on a task, the work type's
 * "facturable por defecto" for one without. `undefined` when it does not know it (the tasks did not
 * load or do not have the task of the hour; no work type chosen yet, or not among those read): the box
 * shows indeterminate instead of a guess, and the server decides on save with the same rule.
 */
export function billableShown(
  chosen: boolean | null,
  choice: { taskId: string | null; workTypeId: string | null },
  tasks: readonly Pick<TaskSummary, 'id' | 'isBillable'>[] | undefined,
  workTypes: readonly Pick<WorkType, 'id' | 'defaultIsBillable'>[] | undefined,
): boolean | undefined {
  return chosen ?? knownProposal(choice, tasks, workTypes);
}

/**
 * `AM-382`, rule 2: the "Facturable" a dialog that creates an hour sends: what it shows
 * ({@link billableShown}). When that is `undefined` it sends nothing, and the server applies the same
 * rule with the task and the work type it reads, instead of the console writing a guess. Where the
 * tenant does not invoice every hour is recorded not billable (`AM-204`), whatever the box said.
 */
export function billableToSend(
  chosen: boolean | null,
  choice: { taskId: string | null; workTypeId: string | null },
  tasks: readonly Pick<TaskSummary, 'id' | 'isBillable'>[] | undefined,
  workTypes: readonly Pick<WorkType, 'id' | 'defaultIsBillable'>[] | undefined,
  commercialBillingEnabled: boolean,
): boolean | undefined {
  if (!commercialBillingEnabled) {
    return false;
  }
  return billableShown(chosen, choice, tasks, workTypes);
}

/** What the edit form holds, as the person left it. */
export interface TimeEntryEditForm {
  workDate: string | null;
  workTypeId: string | null;
  taskId: string | null;
  minutes: number | string;
  description: string;
  /** `undefined` where the form offers no "Facturable" (billing is off): the intent is not touched. */
  isBillable: boolean | undefined;
  /** `AM-386`: the reason chosen for an unticked hour, `null` for none; `undefined` where not offered. */
  nonBillableReasonId?: string | null;
}

/**
 * The merge patch of an edit (`PATCH /time-entries/{id}`): only the fields that differ from the
 * hour as the server last returned it, so a task that is no longer open, or a work type that was
 * deactivated, is not sent again when nobody changed it. `affectsApproval` says whether any of the
 * fields that move the approval of an approved hour changed (duration, day, task, work type or
 * billing intent): the description alone never does (`AM-294`).
 */
export function timeEntryEditChanges(
  detail: Pick<
    TimeEntryDetail,
    | 'workDate'
    | 'workType'
    | 'taskId'
    | 'durationMinutes'
    | 'description'
    | 'billableIntent'
    | 'nonBillableReason'
  >,
  form: TimeEntryEditForm,
): { patch: UpdateTimeEntryInput; affectsApproval: boolean } {
  const patch: UpdateTimeEntryInput = {};
  const minutes = Number(form.minutes);
  if (minutes !== detail.durationMinutes) {
    patch.durationMinutes = minutes;
  }
  if (form.workDate !== null && form.workDate !== detail.workDate) {
    patch.workDate = form.workDate;
  }
  if (form.workTypeId !== null && form.workTypeId !== detail.workType.id) {
    patch.workTypeId = form.workTypeId;
  }
  if (form.taskId !== (detail.taskId ?? null)) {
    patch.taskId = form.taskId;
  }
  if (form.isBillable !== undefined && form.isBillable !== detail.billableIntent) {
    patch.isBillable = form.isBillable;
  }
  const affectsApproval = Object.keys(patch).length > 0;

  const description = form.description.trim();
  if (description !== (detail.description ?? '').trim()) {
    patch.description = description === '' ? null : description;
  }

  // `AM-386`: the reason of an unticked hour does not touch its approval, as the description does not.
  if (
    form.isBillable === false &&
    form.nonBillableReasonId !== undefined &&
    form.nonBillableReasonId !== (detail.nonBillableReason?.id ?? null)
  ) {
    patch.nonBillableReasonId = form.nonBillableReasonId;
  }

  return { patch, affectsApproval };
}

/**
 * The first day of the current week, in the zone the console shows dates in, as the ISO date the
 * filter takes. The weekday is the one of today's calendar day, not of the instant in UTC, and the
 * week starts on the tenant's own first day (`AM-361`): the day its calendars start on, Monday
 * unless it chose another. It was always Monday, beside calendars that could start on Sunday.
 */
export function startOfWeek(
  now: Date = new Date(),
  firstDayOfWeek: DayOfWeek = getActiveFirstDayOfWeek(),
): string {
  return startOfCurrentWeek(now, firstDayOfWeek);
}

/**
 * The filters each tab of "Mis horas" applies (13 §4.6): built apart from the query itself so
 * `MyTimePage` can fold in its own date range on the "Todas" tab, the only one with no date
 * boundary of its own (P060).
 */
export function myTimeFilters(
  view: TimeView,
  userId: string | undefined,
): Record<string, string | undefined> {
  const filters: Record<string, string | undefined> = { agentUserId: userId };
  // `AM-632` (model, pieza Aprobación; texto 121, A): the view Mis horas opens on, "Pendientes", lists the rejected
  // hours too, with their reason and "Corregir": they are the ones their author still has to act on.
  if (view === 'pending') {
    filters['approvalStatus[in]'] = 'pending,rejected';
  }
  if (view === 'week') {
    filters['workDate[from]'] = startOfWeek();
  }

  return filters;
}

/**
 * The hours of a ticket for its "Tiempo" tab, fifty at a time with "Cargar más" (`AM-351`): the
 * tab used to read the last fifty and nothing else, so a ticket with more hid the oldest ones and
 * added up only those it had.
 */
export function ticketTimeQueryOptions(ticketId: string) {
  return infiniteQueryOptions({
    queryKey: ['time-entries', 'ofTicket', ticketId] as const,
    queryFn: ({ pageParam, signal }) =>
      timeEntriesApi.list(
        {
          sort: '-workDate',
          limit: 50,
          filters: { ticketId },
          ...(pageParam === '' ? {} : { cursor: pageParam }),
        },
        signal,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.page.nextCursor ?? undefined,
  });
}

/**
 * The total of the "Tiempo" tab of a ticket (`AM-351`) and of "Mis horas" (`AM-379`): the minutes the
 * server adds up over every hour the list leaves, not only the pages loaded, less the rejected ones. A
 * rejected hour is listed (its author corrects it there) but never counts, the same rule as the column
 * "Horas" of the "Tareas" tab (`TaskSummary.loggedMinutes`) and the card "Mis horas" of "Mi trabajo".
 */
export function countedMinutesOf(page: Pick<TimeEntryPage, 'totalMinutes' | 'rejectedMinutes'>): {
  counted: number;
  rejected: number;
} {
  return { counted: page.totalMinutes - page.rejectedMinutes, rejected: page.rejectedMinutes };
}

/**
 * How much of the ticket's hours this actor's permissions let `/time-entries?ticketId=` answer
 * (07 §3.2, `TimeEntryActor.ScopeOf`): "none" is refused outright, "partial" is only their own or
 * their team's, "all" is the whole tenant.
 */
export function timeVisibilityOf(permissions: ReadonlySet<string>): 'none' | 'partial' | 'all' {
  if (permissions.has('time_entries.view_all') || permissions.has('time_entries.manage')) {
    return 'all';
  }
  if (permissions.has('time_entries.view_team') || permissions.has('time_entries.approve')) {
    return 'partial';
  }
  return permissions.has('time_entries.view_own') || permissions.has('time_entries.create')
    ? 'partial'
    : 'none';
}

/**
 * `AM-465`: the work type the dialogs that create an hour preselect for the ticket and the task chosen
 * ("Tipo de trabajo propuesto"), read again when the task changes.
 */
export function proposedWorkTypeQueryOptions(
  ticketId: string,
  taskId: string | null,
  enabled = true,
) {
  return queryOptions({
    queryKey: ['time-entries', 'proposed-work-type', ticketId, taskId] as const,
    queryFn: ({ signal }) => timeEntriesApi.proposedWorkType(ticketId, taskId, signal),
    enabled,
  });
}

/**
 * `AM-465`: the work type a dialog that creates an hour shows: the one the person chose, or, while they have not
 * touched the field, the proposed one when it is among the active work types offered.
 */
export function shownWorkTypeId(
  chosen: string | null,
  proposed: string | null | undefined,
  offered: readonly Pick<WorkType, 'id'>[] | undefined,
): string | null {
  if (chosen !== null) {
    return chosen;
  }
  return proposed != null && (offered ?? []).some((type) => type.id === proposed) ? proposed : null;
}

/**
 * The clock the person has running. It is polled while it runs so the header keeps counting
 * without the browser inventing the number: the elapsed minutes are always the server's - except
 * offline (`AM-132 point 13`, D17), where the poll cannot land and {@link localElapsedMinutes}
 * takes over until the connection comes back.
 */
export function currentTimerQueryOptions() {
  return queryOptions({
    queryKey: ['timers', 'current'],
    queryFn: ({ signal }) => timersApi.current(signal),
    refetchInterval: 60_000,
  });
}

/**
 * The clock computed from `startedAt` alone, for the stretch a technician spends offline. `AM-462`: rounded as
 * the hour it leaves and as the server shows it, to the nearest minute (half up) and at least one once it ran.
 */
export function localElapsedMinutes(startedAt: string, now: Date = new Date()): number {
  const elapsedMs = now.getTime() - new Date(startedAt).getTime();
  return elapsedMs <= 0 ? 0 : Math.max(1, Math.round(elapsedMs / 60_000));
}

/** What the console says when a write on an hour fails (07 §2.1). */
/** Codes of 10 §9 and 10 §4.5 that deserve their own sentence instead of the generic one (`AM-182`). */
const SPECIFIC_ERRORS: Record<string, string> = {
  task_not_in_ticket: 'time:error.taskNotInTicket',
  task_not_open: 'time:error.taskNotOpen',
  task_required: 'time:error.taskRequired',
  ticket_cancelled: 'time:error.ticketCancelled',
  ticket_closed_requires_adjust: 'time:error.ticketClosed',
  work_date_in_future: 'time:error.futureDate',
  daily_limit_exceeded: 'time:error.dailyLimit',
  retroactive_requires_manage: 'time:error.oldWork',
  time_entry_in_period: 'time:error.inPeriod',
  // `AM-460` (C08-12): a billed hour is corrected with an adjustment, and the refusal says so.
  time_entry_is_locked: 'time:detail.locked',
  adjustment_below_zero: 'time:error.adjustmentBelowZero',
  adjustment_minutes_required: 'time:error.adjustmentMinutesRequired',
  adjustment_requires_locked_entry: 'time:error.adjustmentRequiresLockedEntry',
  idempotency_request_in_progress: 'time:error.duplicate',
};

const WARNINGS = [
  'ticket_closed',
  'ticket_resolved',
  'overlapping_entries',
  'daily_hours_high',
] as const;

/**
 * The sentence a write deserves beyond "saved": the rate that did not resolve and the soft
 * validations of 10 §9 that fired. `null` when there is nothing to say.
 */
export function timeWarnings(warnings: readonly string[], t: Translate): string | null {
  const sentences = [
    ...(warnings.includes('rate_unresolved') ? [t('time:detail.unresolved')] : []),
    ...WARNINGS.filter((code) => warnings.includes(code)).map((code) => t(`time:warning.${code}`)),
  ];
  return sentences.length === 0 ? null : sentences.join(' ');
}

/**
 * Whether a stop or a discard found the clock already closed (`timer_already_stopped`): by another
 * screen, or by the cancellation of its ticket, which keeps its hour (AM-378). The screen reads the
 * clock again, so it stops showing one that no longer runs.
 */
export function isTimerGone(error: unknown): boolean {
  return error instanceof ApiError && error.errorCode === 'timer_already_stopped';
}

/**
 * What the stop or the discard of a clock says when it fails. Two refusals have a sentence of their
 * own there: the clock that is no longer running, and the one left running on a ticket cancelled
 * before AM-378, which takes no hour and can only be discarded.
 */
export function timerProblem(error: unknown, t: Translate): string {
  if (isTimerGone(error)) {
    return t('time:error.timerAlreadyStopped');
  }
  if (
    error instanceof ApiError &&
    [error.errorCode, ...Object.values(error.errors).flat()].includes('ticket_cancelled')
  ) {
    return t('time:error.timerOnCancelledTicket');
  }
  return timeProblem(error, t);
}

/**
 * The refusal of "Recalcular el precio" (`AM-665`, C10-02): an hour whose work type is inactive or gone
 * says why it was not priced again and what to do; the rest read as any other refusal of an hour.
 */
export function recalculateProblem(error: unknown, t: Translate): string {
  if (
    error instanceof ApiError &&
    [error.errorCode, ...Object.values(error.errors).flat()].includes('work_type_required')
  ) {
    return t('time:error.recalculateWorkType');
  }
  return timeProblem(error, t);
}

export function timeProblem(error: unknown, t: Translate): string {
  if (!(error instanceof ApiError)) {
    return t('common:error.generic');
  }
  if (error.errorCode === 'timer_already_running') {
    return t('time:error.timerRunning');
  }
  const codes = [error.errorCode, ...Object.values(error.errors).flat()];
  const specific = codes.map((code) => SPECIFIC_ERRORS[code]).find((key) => key !== undefined);
  if (specific !== undefined) {
    return t(specific);
  }
  if (error.errorCode === ERROR_CODES.concurrentModification) {
    return t('time:error.concurrent');
  }
  if (error.errorCode === ERROR_CODES.invalidStateTransition) {
    return t('time:error.conflict');
  }
  if (error.errorCode === ERROR_CODES.forbidden) {
    return t('time:error.forbidden');
  }
  if (error.status === 404) {
    return t('time:error.notFound');
  }
  if (error.status === 422) {
    return t('time:error.requirement');
  }
  return t('common:error.generic');
}
